import { useState, useEffect, useCallback } from 'react';
import { accountantService } from '../services/accountantService';
import { switchCompany } from '../utils/contextManager';
import { Invitation, LinkedCompany, FiscalAlert, OfflineClientPayload } from '../types/accountant';
import { pipelineService } from '../services/pipelineService';

import { persistNavigationIntent } from '../utils/navigationIntent';
import { dispatchCustomEvent, listener } from '../utils/dispatch';

/**
 * Custom Hook: useAccountantHub
 * Manages the state and business logic for the Accountant Hub.
 */
export function useAccountantHub() {
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [linkedCompanies, setLinkedCompanies] = useState<LinkedCompany[]>([]);
    const [fiscalAlerts, setFiscalAlerts] = useState<FiscalAlert[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [invsResponse, companiesResponse, alertsResponse] = await Promise.all([
                accountantService.getMyInvitations(),
                accountantService.getLinkedCompanies(),
                accountantService.getFiscalAlerts()
            ]);

            // Adaptation: HttpClient returns the whole body { success: true, data: { data: [], total: ... } }
            // or just { success: true, data: [] }

            // For invitations (/invitations/my), it's success_response(data=list)
            const invs = invsResponse.data || [];

            // For linked companies (/access), it's paginated_response which is success_response(data={data: [], total: ...})
            const companies = companiesResponse.data?.data || companiesResponse.data || [];

            const companiesWithCounts = await Promise.all(
                companies.map(async (company: any) => {
                    try {
                        const draftsRes = await pipelineService.getDrafts(1, 1, 'pending', company.owner_user_id);
                        const count = draftsRes.data?.total ?? draftsRes.total ?? 0;
                        return { ...company, pending_drafts_count: count };
                    } catch (e) {
                        return { ...company, pending_drafts_count: 0 };
                    }
                })
            );

            // For alerts (/accountant/fiscal-alerts), it's success_response(data=list)
            const alerts = alertsResponse.data || [];

            setInvitations(Array.isArray(invs) ? invs : []);
            setLinkedCompanies(Array.isArray(companiesWithCounts) ? companiesWithCounts : []);
            setFiscalAlerts(Array.isArray(alerts) ? alerts : []);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            console.error("Error loading Accountant Hub data:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleAccept = async (id: number) => {
        try {
            setIsLoading(true);
            await accountantService.acceptInvitation(id);
            dispatchCustomEvent('popup', {
                message: 'Invitación aceptada correctamente',
                type: 'success'
            });
            await loadData();
        } catch (err) {
            dispatchCustomEvent('popup', {
                message: 'No se pudo aceptar la invitación',
                type: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Logic to execute the rejection after user confirmation.
     */
    const performReject = useCallback(async (id: number) => {
        try {
            setIsLoading(true);
            await accountantService.rejectInvitation(id);
            dispatchCustomEvent('popup', {
                message: 'Invitación rechazada',
                type: 'success'
            });
            await loadData();
        } catch (err) {
            dispatchCustomEvent('popup', {
                message: 'No se pudo rechazar la invitación',
                type: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    }, [loadData]);

    useEffect(() => {
        const clean = listener('reject-invitation-confirm-event', (e: any) => {
            const id = e.detail.data;
            if (id) performReject(id);
        });
        return () => clean();
    }, [performReject]);

    const handleReject = (id: number) => {
        // According to ConfirmPopUp logic, we send the data payload
        dispatchCustomEvent('notification-confirm-reject-invitation-confirm-event', { data: id });
    };

    const handleEnterCompany = (ownerUserId: string, targetPath = '/') => {
        // In the project layout, a global full-screen loader usually handles this visually
        // but we ensure the local state is also loading if needed.
        setIsLoading(true);
        switchCompany(ownerUserId, targetPath);
    };

    const handleHealGaps = async (companyId: number) => {
        try {
            setIsLoading(true);
            const res = await accountantService.healGaps(companyId);

            if (res.success) {
                dispatchCustomEvent('popup', {
                    message: res.message || `Sincronización exitosa: ${res.data.recovered_count} facturas recuperadas.`,
                    type: 'success'
                });
                await loadData();
            } else {
                dispatchCustomEvent('popup', {
                    message: res.data?.message || 'No se pudo completar la sincronización',
                    type: 'error'
                });
            }
        } catch (e) {
            dispatchCustomEvent('popup', {
                message: 'Ocurrió un error inesperado durante la sincronización',
                type: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    interface AlertStrategy {
        execute: (companyId: string) => void;
    }

    const handleAlertClick = (companyId: string, alertKey: string) => {
        const strategies: Record<string, AlertStrategy> = {
            rejected_invoices: {
                execute: (id) => {
                    persistNavigationIntent('/pos', 2);
                    handleEnterCompany(id, '/pos');
                }
            },
            resolutions_warning: {
                execute: (id) => {
                    persistNavigationIntent('/invoice', 3);
                    handleEnterCompany(id, '/invoice');
                }
            },
            negative_inventory: {
                execute: (id) => {
                    persistNavigationIntent('/stock', 0, { onlyNegativeStock: true });
                    handleEnterCompany(id, '/stock');
                }
            },
            sequence_gaps: {
                execute: (id) => {
                    handleHealGaps(Number(id));
                }
            }
        };

        strategies[alertKey]?.execute(companyId);
    };

    const handleCreateOfflineClient = async (payload: OfflineClientPayload) => {
        try {
            setIsLoading(true);
            const res = await accountantService.createOfflineClient(payload);
            if (res.success) {
                dispatchCustomEvent('popup', {
                    message: 'Cliente offline creado exitosamente',
                    type: 'success'
                });
                await loadData();
                return true;
            } else {
                dispatchCustomEvent('popup', {
                    message: res.message || 'Error al crear el cliente offline',
                    type: 'error'
                });
                return false;
            }
        } catch (err: any) {
            const errorMsg = err?.response?.data?.message || err?.message || 'Error al crear el cliente offline';
            dispatchCustomEvent('popup', {
                message: errorMsg,
                type: 'error'
            });
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        invitations,
        linkedCompanies,
        fiscalAlerts,
        isLoading,
        error,
        handleAccept,
        handleReject,
        handleEnterCompany,
        handleAlertClick,
        handleCreateOfflineClient,
        refresh: loadData,
    };
}
